import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { injectIntl } from 'react-intl';
import styled from 'styled-components/macro';
import { ColorVariant } from '../../theme';
import { IconVariant } from '../Icon/Icon';
import { LayoutRowHorizontalVariant } from '../Layout/Layout';
import Text, { TextVariant } from '../Text/Text';
import PaginationControl from '../PaginationControl/PaginationControl';
import IconButton, { IconButtonColorVariant, IconButtonSizeVariant, } from '../IconButton/IconButton';
import { useLocation } from 'react-router-dom';
import useBreakpoints from '../../util/useMediaBreakpoints';
import ListingCard from '../ListingCard/ListingCard';
import Button2024, { ButtonSizeVariant, ButtonVariant, } from '../Button2024/Button2024';
const Wrapper = styled.div `
  ${props => props.enableBottomSpacing && 'margin-bottom: 50px;'}

  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media ${props => props.theme.device.tablet} {
    gap: 40px;
  }
`;
const Title = styled(Text) `
  display: ${props => (props.hideTitleOnMobile ? 'none' : 'block')};
  @media ${props => props.theme.device.tabletL} {
    display: block;
  }
`;
const Header = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const Controls = styled.div `
  display: flex;
  gap: 8px;

  @media ${props => props.theme.device.tablet} {
    gap: 24px;
  }
`;
const PaginationWrapper = styled.div `
  display: flex;
  justify-content: center;
`;
const FooterWrapper = styled.div `
  margin-bottom: 60px;
  display: flex;
  justify-content: center;
`;
const Grid = styled.div `
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: 40px;
  column-gap: 20px;

  @media ${props => props.theme.device.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }

  @media ${props => props.theme.device.desktop} {
    grid-template-columns: repeat(4, 1fr);
  }
`;
const SectionContentGrid = ({ titleTextVariant = TextVariant.H4, title, intl, colorVariant = ColorVariant.Cave, buttonVariant = ButtonVariant.Primary, buttonSizeVariant = ButtonSizeVariant.Large, buttonIconVariant, buttonLabel, buttonTargetRoute, buttonSearchParams, buttonExternalLink, horizontalVariant = LayoutRowHorizontalVariant.ContainedLeft, hideControls = false, hideTitleOnMobile, enableBottomSpacing = false, listings = [], }) => {
    var _a;
    const { isDesktop, isTablet } = useBreakpoints();
    const getElementsPerPage = () => {
        if (isDesktop) {
            return 12;
        }
        if (isTablet) {
            return 9;
        }
        return 6;
    };
    const elementsPerPage = getElementsPerPage();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const pageFromQuery = parseInt((_a = queryParams.get('page')) !== null && _a !== void 0 ? _a : '1', 10);
    const totalPages = Math.ceil(listings.length / elementsPerPage);
    return (_jsxs(Wrapper, Object.assign({ enableBottomSpacing: enableBottomSpacing, "data-testid": `SectionContentGrid_${title}` }, { children: [_jsxs(Header, Object.assign({ horizontalVariant: horizontalVariant }, { children: [_jsx(Title, Object.assign({ "data-testid": `SectionContentGrid_${title}Title`, variant: titleTextVariant, colorVariant: colorVariant, hideTitleOnMobile: hideTitleOnMobile, as: "h2" }, { children: title })), !hideControls && (_jsxs(Controls, { children: [_jsx(IconButton, { iconVariant: IconVariant.Arrow_M_Left, iconButtonSizeVariant: IconButtonSizeVariant.Small, ariaLabel: intl.formatMessage({
                                    id: 'SectionContentGrid.ariaLabelPrevious',
                                }), colorVariant: IconButtonColorVariant.Light, disabled: pageFromQuery === 1, link: pageFromQuery > 1
                                    ? {
                                        to: {
                                            pathname: location.pathname,
                                            search: `?page=${pageFromQuery - 1}`,
                                        },
                                        replace: true,
                                    }
                                    : undefined }), _jsx(IconButton, { iconVariant: IconVariant.Arrow_M_Right, iconButtonSizeVariant: IconButtonSizeVariant.Small, ariaLabel: intl.formatMessage({
                                    id: 'SectionContentGrid.ariaLabelForward',
                                }), colorVariant: IconButtonColorVariant.Light, disabled: pageFromQuery === totalPages, link: pageFromQuery < totalPages
                                    ? {
                                        to: {
                                            pathname: location.pathname,
                                            search: `?page=${pageFromQuery + 1}`,
                                        },
                                        replace: true,
                                    }
                                    : undefined })] }))] })), _jsx(Grid, { children: listings
                    .slice((pageFromQuery - 1) * elementsPerPage, pageFromQuery * elementsPerPage)
                    .map(listing => {
                    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x;
                    return (_jsx(ListingCard, { id: listing.id, title: (_a = listing.attributes) === null || _a === void 0 ? void 0 : _a.title, campStyles: (_c = (_b = listing.attributes) === null || _b === void 0 ? void 0 : _b.publicData) === null || _c === void 0 ? void 0 : _c.campStyle, rating: (_e = (_d = listing === null || listing === void 0 ? void 0 : listing.attributes) === null || _d === void 0 ? void 0 : _d.metadata) === null || _e === void 0 ? void 0 : _e.rating, numberOfRatings: (_g = (_f = listing === null || listing === void 0 ? void 0 : listing.attributes) === null || _f === void 0 ? void 0 : _f.metadata) === null || _g === void 0 ? void 0 : _g.numberOfRatings, verified: !!((_j = (_h = listing.attributes) === null || _h === void 0 ? void 0 : _h.metadata) === null || _j === void 0 ? void 0 : _j.isVerified), instantBooking: (_l = (_k = listing.attributes) === null || _k === void 0 ? void 0 : _k.publicData) === null || _l === void 0 ? void 0 : _l.instantBookingAvailable, price: (_m = listing.attributes) === null || _m === void 0 ? void 0 : _m.price, images: listing.images.map((image) => { var _a, _b; return (_b = (_a = image.attributes) === null || _a === void 0 ? void 0 : _a.variants['square-small2x']) === null || _b === void 0 ? void 0 : _b.url; }), titleComponent: "h3", addressComponents: (_q = (_p = (_o = listing.attributes) === null || _o === void 0 ? void 0 : _o.publicData) === null || _p === void 0 ? void 0 : _p.location) === null || _q === void 0 ? void 0 : _q.address_components, toilet: (_s = (_r = listing.attributes) === null || _r === void 0 ? void 0 : _r.publicData) === null || _s === void 0 ? void 0 : _s.toiletAvailable, power: (_u = (_t = listing.attributes) === null || _t === void 0 ? void 0 : _t.publicData) === null || _u === void 0 ? void 0 : _u.powerSupplyAvailable, fireplace: (_w = (_v = listing.attributes) === null || _v === void 0 ? void 0 : _v.publicData) === null || _w === void 0 ? void 0 : _w.openFireAllowed, topHost: (_x = listing.author.attributes.profile.metadata) === null || _x === void 0 ? void 0 : _x.isTopHost }, listing.id.uuid));
                }) }), _jsx(PaginationWrapper, { children: _jsx(PaginationControl, { activePage: pageFromQuery, totalPages: totalPages, link: {
                        to: {
                            pathname: location.pathname,
                        },
                        replace: true,
                    } }) }), buttonLabel && (_jsx(FooterWrapper, { children: _jsx(Button2024, { label: buttonLabel, variant: buttonVariant, sizeVariant: buttonSizeVariant, iconOnLeft: buttonIconVariant, targetRoute: buttonTargetRoute, searchParams: buttonSearchParams, externalLink: buttonExternalLink }) }))] })));
};
export default injectIntl(SectionContentGrid);
